import { useState, type CSSProperties, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Logo } from '../components/ui/Logo.js';
import { Icon } from '../components/ui/Icon.js';
import { Button } from '../components/ui/Button.js';

type Step = 'credentials' | 'totp';

const inputStyle: CSSProperties = {
  width: '100%',
  background: 'var(--surface-2)',
  border: '1px solid var(--line-soft)',
  borderRadius: 12,
  padding: '12px 14px',
  fontSize: 14,
  color: 'var(--ink)',
  fontFamily: 'inherit',
  outline: 'none',
};

const labelStyle: CSSProperties = {
  display: 'block',
  marginBottom: 6,
  color: 'var(--ink-2)',
  fontSize: 13,
  fontWeight: 500,
};

const errorStyle: CSSProperties = {
  fontSize: 13,
  color: 'var(--danger)',
  background: 'color-mix(in oklab, var(--danger) 14%, transparent)',
  border: '1px solid color-mix(in oklab, var(--danger) 32%, transparent)',
  borderRadius: 12,
  padding: '10px 14px',
};

export function LoginPage() {
  const [step, setStep]                 = useState<Step>('credentials');
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [totpCode, setTotpCode]         = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [error, setError]               = useState('');
  const [loading, setLoading]           = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();

  async function handleCredentials(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    const form = e.currentTarget as HTMLFormElement;
    const emailVal    = (form.elements.namedItem('email')    as HTMLInputElement)?.value ?? email;
    const passwordVal = (form.elements.namedItem('password') as HTMLInputElement)?.value ?? password;
    try {
      const res = await api.post<{ success?: boolean; totp_required?: boolean; pendingToken?: string }>(
        '/api/auth/login',
        { email: emailVal, password: passwordVal },
      );
      if (res.totp_required && res.pendingToken) {
        setPendingToken(res.pendingToken);
        setStep('totp');
      } else {
        await qc.invalidateQueries({ queryKey: ['profile'] });
        navigate('/dashboard', { replace: true });
      }
    } catch (err: any) {
      setError(err.body?.error ?? 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleTotp(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/api/auth/totp/validate', { pendingToken, code: totpCode });
      await qc.invalidateQueries({ queryKey: ['profile'] });
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.body?.error ?? 'Incorrect code. Please try again.');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{
        background: 'var(--bg-deep)',
        backgroundImage: 'radial-gradient(60% 40% at 50% 0%, var(--accent-soft), transparent 70%)',
      }}
    >
      <div
        className="w-full max-w-md p-8 sm:p-10"
        style={{
          background: 'var(--surface)',
          borderRadius: 22,
          border: '1px solid var(--line-soft)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        {/* ── Brand mark + back affordance ─────────────────────────── */}
        <div className="flex items-center justify-between mb-8">
          <Logo />
          <Link
            to="/"
            className="text-xs font-mono hover:underline inline-flex items-center gap-1.5"
            style={{ color: 'var(--ink-4)' }}
          >
            <Icon name="chev" size={11} style={{ transform: 'rotate(180deg)' }} />
            home
          </Link>
        </div>

        {step === 'credentials' && (
          <>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--accent)',
                marginBottom: 10,
              }}
            >
              Welcome back
            </div>
            <h1 className="h-display" style={{ fontSize: 36, lineHeight: 1.05, margin: 0, color: 'var(--ink)' }}>
              Sign{' '}
              <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>in.</span>
            </h1>
            <p className="text-sm mt-3 mb-7" style={{ color: 'var(--ink-3)' }}>
              Pick up where you left off. Your kitchen's right where you left it.
            </p>

            <form onSubmit={handleCredentials} className="space-y-4">
              <div>
                <label htmlFor="login-email" style={labelStyle}>Email</label>
                <input
                  id="login-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="login-password" style={{ ...labelStyle, marginBottom: 0 }}>Password</label>
                  <Link
                    to="/forgot-password"
                    className="text-xs hover:underline"
                    style={{ color: 'var(--ink-4)' }}
                  >
                    Forgot password?
                  </Link>
                </div>
                <input
                  id="login-password"
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={inputStyle}
                />
              </div>

              {error && <p style={errorStyle}>{error}</p>}

              <Button
                type="submit"
                disabled={loading}
                className="w-full"
                style={{ marginTop: 4 }}
              >
                {loading ? 'Signing in…' : 'Sign in'}
                {!loading && <Icon name="arrow" size={14} />}
              </Button>
            </form>

            <p className="text-center mt-6" style={{ fontSize: 13, color: 'var(--ink-3)' }}>
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="hover:underline"
                style={{ color: 'var(--accent)', fontWeight: 500 }}
              >
                Start your 30-day trial
              </Link>
            </p>
          </>
        )}

        {step === 'totp' && (
          <>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--accent)',
                marginBottom: 10,
              }}
            >
              Two-factor
            </div>
            <h1 className="h-display" style={{ fontSize: 32, lineHeight: 1.05, margin: 0, color: 'var(--ink)' }}>
              One more{' '}
              <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>code.</span>
            </h1>
            <p className="text-sm mt-3 mb-7" style={{ color: 'var(--ink-3)' }}>
              Open Google Authenticator and enter the 6-digit code for Platterplan.
            </p>

            <form onSubmit={handleTotp} className="space-y-4">
              <div>
                <label htmlFor="login-totp" style={labelStyle}>Authenticator code</label>
                <input
                  id="login-totp"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  style={{
                    ...inputStyle,
                    textAlign: 'center',
                    letterSpacing: '0.6em',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 22,
                    paddingTop: 14,
                    paddingBottom: 14,
                  }}
                />
              </div>

              {error && <p style={errorStyle}>{error}</p>}

              <Button
                type="submit"
                disabled={loading || totpCode.length !== 6}
                className="w-full"
                style={{ marginTop: 4 }}
              >
                {loading ? 'Verifying…' : 'Verify'}
                {!loading && <Icon name="arrow" size={14} />}
              </Button>

              <button
                type="button"
                onClick={() => { setStep('credentials'); setError(''); setTotpCode(''); }}
                className="w-full hover:underline inline-flex items-center justify-center gap-1.5"
                style={{
                  fontSize: 13,
                  color: 'var(--ink-3)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  paddingTop: 4,
                }}
              >
                <Icon name="chev" size={11} style={{ transform: 'rotate(180deg)' }} />
                Back to sign in
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
